import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowRightCircle, BedDouble, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { track } from '../../services/analytics';
import { filterBookableRooms, bookableTag } from '../../utils/roomAvailability';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Converts a lead into an active booking.
 * - Multiple leads can stay interested in the same room (no exclusivity at lead level).
 * - Only ONE booking per room at a time (server enforces it with a 409).
 * - Token payment is OPTIONAL — "Skip for now" is one click away.
 */
const ConvertToBookingModal = ({ open, onClose, onSuccess, lead, token }) => {
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);

  // Prefer the first interested room (if lead had any) as the default
  const firstInterested = lead?.interested_rooms?.[0];
  const [form, setForm] = useState({
    property_id: firstInterested?.property_id || '',
    room_id: firstInterested?.id || '',
    check_in_date: lead?.move_in_date || today(),
    token_amount: '',
    token_paid: false,
    token_method: 'upi',
    rent_amount: '',
    deposit_amount: '',
    notice_period_days: 30,
    notes: '',
  });

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchProperties = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/properties`, h);
      setProperties(r.data);
    } catch { setProperties([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (open) fetchProperties(); }, [open, fetchProperties]);

  useEffect(() => {
    if (!form.property_id) { setRooms([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/properties/${form.property_id}/rooms`, h);
        setRooms(r.data);
      } catch { setRooms([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.property_id, token]);

  // Autofill rent/deposit from selected room
  useEffect(() => {
    if (!form.room_id) return;
    const r = rooms.find(x => x.id === form.room_id);
    if (!r) return;
    setForm(prev => ({
      ...prev,
      rent_amount: prev.rent_amount || r.rent_per_bed || '',
      deposit_amount: prev.deposit_amount || (r.rent_per_bed ? r.rent_per_bed * 2 : ''),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.room_id]);

  const submit = async (paid) => {
    if (!form.property_id || !form.room_id) {
      toast({ title: 'Pick a property and room', variant: 'destructive' });
      return;
    }
    if (paid && (!form.token_amount || parseFloat(form.token_amount) <= 0)) {
      toast({ title: 'Enter token amount to mark as paid', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/convert-to-booking`, {
        property_id: form.property_id,
        room_id: form.room_id,
        check_in_date: form.check_in_date,
        token_amount: parseFloat(form.token_amount) || 0,
        token_paid: !!paid,
        token_method: paid ? form.token_method : null,
        rent_amount: parseFloat(form.rent_amount) || 0,
        deposit_amount: parseFloat(form.deposit_amount) || 0,
        notice_period_days: parseInt(form.notice_period_days, 10) || 30,
        notes: form.notes || null,
      }, h);
      toast({
        title: 'Converted to Booking',
        description: paid
          ? `Booking confirmed. Token ₹${form.token_amount} recorded.`
          : 'Booking created as pending. Collect token later.',
      });
      emit(EVT.LEAD,    { action: 'converted', id: lead.id });
      emit(EVT.BOOKING, { action: 'created', from_lead: lead.id });
      emit(EVT.ROOM,    { action: 'booking_locked', room_id: form.room_id });
      if (paid && parseFloat(form.token_amount) > 0) {
        // Token payment was just logged in Collections — refresh money pages.
        emit(EVT.PAYMENT, { action: 'token_received', booking_id: res.data?.booking?.id });
      }
      track('lead_converted_to_booking', {
        lead_id: lead.id,
        token_paid: !!paid,
        token_amount: parseFloat(form.token_amount) || 0,
      });
      if (onSuccess) onSuccess(res.data);
      onClose();
    } catch (e) {
      // Aggressive debug logging — surfaces in DevTools Console so we can
      // see the exact HTTP status, response body, and request payload for
      // any failed conversion. Remove once stable.
      // eslint-disable-next-line no-console
      console.error('[ConvertToBooking] FAILED', {
        status: e.response?.status,
        data: e.response?.data,
        message: e.message,
        request_payload: {
          property_id: form.property_id,
          room_id: form.room_id,
          check_in_date: form.check_in_date,
          token_paid: !!paid,
          token_amount: parseFloat(form.token_amount) || 0,
          rent_amount: parseFloat(form.rent_amount) || 0,
          deposit_amount: parseFloat(form.deposit_amount) || 0,
          notice_period_days: parseInt(form.notice_period_days, 10) || 30,
        },
      });

      const raw = e.response?.data?.detail;
      const detail = (Array.isArray(raw) ? raw.map(x => x?.msg || JSON.stringify(x)).join(', ') : raw)
        || e.message
        || 'Conversion failed';
      const is409 = e.response?.status === 409;
      toast({
        title: is409 ? 'Room already booked' : `Cannot convert${e.response?.status ? ` (${e.response.status})` : ''}`,
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Highlight room exclusivity — if any interested room already looks "booked" flag it
  // (We can't perfectly know from frontend; the server returns 409 on submit.)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightCircle className="text-green-600" size={20} /> Convert Lead to Booking
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lead summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
              {lead?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">{lead?.name}</p>
              <p className="text-xs text-gray-500">{lead?.phone}{lead?.email ? ` · ${lead.email}` : ''}</p>
            </div>
            {lead?.interested_rooms?.length > 0 && (
              <div className="text-xs text-gray-500 text-right">
                <p>Interested in:</p>
                <div className="flex flex-wrap gap-1 mt-1 justify-end">
                  {lead.interested_rooms.slice(0, 3).map(r => (
                    <span key={r.id} className="bg-white border border-gray-300 px-1.5 py-0.5 rounded text-xs">
                      {r.property_name} · Rm {r.room_number}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Property + Room */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Property *</Label>
              <select
                data-testid="convert-property"
                value={form.property_id}
                onChange={e => setForm({ ...form, property_id: e.target.value, room_id: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                <option value="">Select property</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Room *</Label>
              <select
                data-testid="convert-room"
                value={form.room_id}
                onChange={e => {
                  const rid = e.target.value;
                  const r = rooms.find(rr => rr.id === rid);
                  // Auto-fill rent + deposit (= 2 months rent) from the selected
                  // room only when the user hasn't already typed something.
                  setForm(prev => ({
                    ...prev,
                    room_id: rid,
                    rent_amount: prev.rent_amount || (r?.rent_per_bed ? String(r.rent_per_bed) : prev.rent_amount),
                    deposit_amount: prev.deposit_amount || (r?.rent_per_bed ? String(r.rent_per_bed * 2) : prev.deposit_amount),
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
                disabled={!form.property_id}
              >
                <option value="">Select room</option>
                {(() => {
                  // Bookable rooms + the lead's pre-selected room (so the
                  // modal still works when the firstInterested room is now
                  // "booked" — owner can still see + change selection).
                  const bookable = filterBookableRooms(rooms);
                  const preIds = (lead?.interested_rooms || []).map(r => r.id).filter(Boolean);
                  const extras = rooms.filter(r =>
                    preIds.includes(r.id) && !bookable.find(b => b.id === r.id)
                  );
                  const list = [...bookable, ...extras];
                  if (list.length === 0) {
                    return <option disabled value="">No bookable rooms in this property</option>;
                  }
                  return list.map(r => {
                    const tag = bookableTag(r) || (r.status || 'check status');
                    return (
                      <option key={r.id} value={r.id}>
                        Room {r.room_number}
                        {r.room_type ? ` · ${r.room_type}` : ''}
                        {` · ₹${r.rent_per_bed || 0}/bed · ${tag}`}
                      </option>
                    );
                  });
                })()}
              </select>
            </div>
          </div>

          {/* Dates + rent */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Check-in Date *</Label>
              <Input data-testid="convert-checkin" type="date" value={form.check_in_date} onChange={e => setForm({ ...form, check_in_date: e.target.value })} required />
            </div>
            <div>
              <Label>Rent / month (₹)</Label>
              <Input data-testid="convert-rent" type="number" min="0" value={form.rent_amount} onChange={e => setForm({ ...form, rent_amount: e.target.value })} />
            </div>
            <div>
              <Label>Deposit (₹)</Label>
              <Input data-testid="convert-deposit" type="number" min="0" value={form.deposit_amount} onChange={e => setForm({ ...form, deposit_amount: e.target.value })} />
            </div>
            <div>
              <Label>Notice Period (T&amp;C)</Label>
              <select
                data-testid="convert-notice-period"
                value={form.notice_period_days}
                onChange={e => setForm({ ...form, notice_period_days: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                title="Days the tenant must give before vacating"
              >
                <option value="7">7 days</option>
                <option value="15">15 days</option>
                <option value="30">30 days</option>
                <option value="45">45 days</option>
              </select>
            </div>
          </div>

          {/* Token — optional */}
          <div className="border border-gray-200 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2">
              <BedDouble size={16} className="text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-800">Token / Advance (optional)</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Token Amount (₹)</Label>
                <Input data-testid="convert-token" type="number" min="0" value={form.token_amount} onChange={e => setForm({ ...form, token_amount: e.target.value })} placeholder="e.g., 5000" />
              </div>
              <div>
                <Label>Payment Method</Label>
                <select data-testid="convert-method" value={form.token_method} onChange={e => setForm({ ...form, token_method: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <AlertTriangle size={11} /> You can skip this now — booking will be created as <span className="font-medium">pending</span> until token is collected.
            </p>
          </div>

          <div>
            <Label>Notes</Label>
            <textarea data-testid="convert-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button
            data-testid="convert-skip-btn"
            type="button"
            variant="outline"
            onClick={() => submit(false)}
            disabled={saving}
            className="flex-1"
          >
            {saving ? 'Saving...' : 'Skip token · Create Pending Booking'}
          </Button>
          <Button
            data-testid="convert-paid-btn"
            type="button"
            onClick={() => submit(true)}
            disabled={saving}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {saving ? 'Saving...' : 'Token Collected · Confirm Booking'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToBookingModal;
